import React, { useState } from 'react';
import axios from 'axios';
import { errorMessage } from '../../utils/staffDocuments';
import { useConfirm, useToast, useUnsavedChanges, DISCARD } from '../ui';
import WhoSigns from './WhoSigns';
import './staffDocuments.css';

const lines = text => text.split('\n').map(l => l.trim()).filter(Boolean);

// A written document's introduction and its sections of rules. Shared by
// adding and editing; idPrefix keeps the field ids apart.
export function SectionsFields({ idPrefix, intro, setIntro, sections, setSections, growRows = false }) {
  const setSection = (i, field, value) => setSections(list => list.map((s, j) => (j === i ? { ...s, [field]: value } : s)));
  return (
    <>
      <div className="bk-label">
        <label htmlFor={`${idPrefix}-intro`}>Introduction</label>
        <span className="sd-hint">Leave a blank line between paragraphs.</span>
        <textarea id={`${idPrefix}-intro`} className="bk-input" rows={growRows ? 5 : 4} value={intro} onChange={e => setIntro(e.target.value)} />
      </div>
      {sections.map((s, i) => (
        <fieldset key={i} className="sd-section">
          <legend>Section {i + 1}</legend>
          <label className="bk-label" htmlFor={`${idPrefix}-h-${i}`}>
            Heading
            <input id={`${idPrefix}-h-${i}`} className="bk-input" value={s.heading} onChange={e => setSection(i, 'heading', e.target.value)} />
          </label>
          <label className="bk-label" htmlFor={`${idPrefix}-i-${i}`}>
            Rules, one per line
            <textarea
              id={`${idPrefix}-i-${i}`}
              className="bk-input"
              rows={growRows ? Math.max(3, s.items.split('\n').length + 1) : 3}
              value={s.items}
              onChange={e => setSection(i, 'items', e.target.value)}
            />
          </label>
          {sections.length > 1 && (
            <button type="button" className="bk-btn bk-btn--outline-danger sd-section__remove" onClick={() => setSections(list => list.filter((_, j) => j !== i))}>
              Remove section
            </button>
          )}
        </fieldset>
      ))}
      <button type="button" className="bk-btn sd-add-section" onClick={() => setSections(list => [...list, { heading: '', items: '' }])}>Add a section</button>
    </>
  );
}

// A club admin edits a document. Written ones: title, wording and what staff
// agree to. PDFs: title and what staff agree to, and the file can be replaced.
// Paragraphs are separated by a blank line; each rule is one line.
export default function DocumentEditor({ doc, online, onSaved, onCancel }) {
  const confirm = useConfirm();
  const toast = useToast();
  const isFile = doc.kind === 'FILE';
  const [title, setTitle] = useState(doc.title);
  const [intro, setIntro] = useState((doc.intro || []).join('\n\n'));
  const [sections, setSections] = useState((doc.sections || []).map(s => ({ heading: s.heading, items: s.items.join('\n') })));
  const [declaration, setDeclaration] = useState(doc.declaration || '');
  const [signerRoles, setSignerRoles] = useState(doc.signerRoles);
  const [newVersion, setNewVersion] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [start] = useState(() => JSON.stringify({ title, intro, sections, declaration, signerRoles, newVersion }));
  const changed = JSON.stringify({ title, intro, sections, declaration, signerRoles, newVersion }) !== start;
  useUnsavedChanges(changed && !busy);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { title: title.trim(), declaration: declaration.trim(), signerRoles, newVersion };
      if (!isFile) {
        body.intro = intro.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
        body.sections = sections.map(s => ({ heading: s.heading.trim(), items: lines(s.items) })).filter(s => s.heading && s.items.length);
      }
      const res = await axios.put(`/api/staff-documents/${doc.id}`, body);
      toast('Document saved');
      onSaved(res.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't save. Try again."));
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (changed && !(await confirm(DISCARD))) return;
    onCancel();
  };

  const reset = async () => {
    const ok = await confirm({
      title: 'Go back to the standard code of conduct?',
      message: "Your club's wording is replaced with the Run The Club code of conduct.",
      confirmLabel: 'Use the standard code',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await axios.delete(`/api/staff-documents/${doc.id}/content`);
      toast('Back to the standard code of conduct');
      onSaved(res.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't reset. Try again."));
      setBusy(false);
    }
  };

  const replaceFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.put(`/api/staff-documents/${doc.id}/file`, form);
      toast('PDF replaced');
      onSaved(res.data);
    } catch (err) {
      setError(errorMessage(err, "Couldn't replace the PDF. Try again."));
      setBusy(false);
    }
  };

  return (
    <form className="sd-form" onSubmit={save}>
      <h2 className="bk-title">Edit the document</h2>
      <label className="bk-label" htmlFor="coc-title">
        Title
        <input id="coc-title" className="bk-input" value={title} onChange={e => setTitle(e.target.value)} />
      </label>

      {!isFile && (
        <SectionsFields idPrefix="coc" intro={intro} setIntro={setIntro} sections={sections} setSections={setSections} growRows />
      )}

      <label className="bk-label" htmlFor="coc-declaration">
        What staff agree to when they sign
        <textarea id="coc-declaration" className="bk-input" rows={3} value={declaration} onChange={e => setDeclaration(e.target.value)} />
      </label>

      <WhoSigns value={signerRoles} onChange={setSignerRoles} disabled={busy} />

      {isFile && (
        <div className="bk-label">
          <label htmlFor="coc-replace">Replace the PDF</label>
          <span className="sd-hint">A new PDF is a new version, so staff are asked to sign again. It's saved straight away.</span>
          <input id="coc-replace" type="file" accept=".pdf,application/pdf" onChange={replaceFile} disabled={busy || !online} />
        </div>
      )}

      <div>
        <label className="sd-check">
          <input type="checkbox" className="bk-checkbox" checked={newVersion} onChange={e => setNewVersion(e.target.checked)} />
          Ask all staff to sign again
        </label>
        <span className="sd-hint">Tick this when the document changes. Leave it for small corrections, so existing signatures still count.</span>
      </div>

      {error && <p className="bk-form-error" role="alert">{error}</p>}
      <div className="bk-actions">
        <button type="submit" className="bk-btn bk-btn--primary" disabled={busy || !online}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="bk-btn" onClick={cancel} disabled={busy}>Cancel</button>
      </div>
      {doc.template && !doc.followsTemplate && (
        <div className="bk-actions">
          <button type="button" className="bk-btn bk-btn--outline-danger" onClick={reset} disabled={busy || !online}>Use the standard code</button>
        </div>
      )}
    </form>
  );
}
